/**
 * Korean spellcheck, running entirely in the browser.
 *
 * Uses Hunspell (compiled to WebAssembly by hunspell-asm) with the
 * hunspell-dict-ko dictionary served from this server (public/spellcheck).
 * Nothing leaves the browser. It catches misspelled words (e.g. 몇일 → 며칠,
 * 할께요 → 할게요) but not spacing or grammar.
 *
 * This file holds the word handling; the checking itself runs in a Web
 * Worker (korean_spellcheck_client.ts → korean_spellcheck_worker.ts).
 */

const MAX_WORDS_TO_CHECK = 1000;
const MAX_SUGGESTIONS = 5;

export interface SpellChecker {
  isCorrect: (word: string) => boolean;
  suggest: (word: string) => string[];
}

export interface SpellingIssue {
  word: string;
  suggestions: string[];
  occurrences: number;
}

// Links, mentions, hashtags and custom emoji are not words to check
const IGNORED_PATTERNS =
  /(?:https?:\/\/\S+)|(?:@[\w.-]+(?:@[\w.-]+)?)|(?:#\S+)|(?::[\w+-]+:)/gu;
const HANGUL_WORD = /[가-힣]+/gu;

/** Extracts the distinct Korean words of a text, in order of appearance. */
export function extractKoreanWords(text: string) {
  const counts = new Map<string, number>();
  const cleaned = text.replace(IGNORED_PATTERNS, ' ');

  for (const match of cleaned.matchAll(HANGUL_WORD)) {
    const word = match[0];
    counts.set(word, (counts.get(word) ?? 0) + 1);
  }

  return counts;
}

export function findSpellingIssues(
  text: string,
  checker: SpellChecker,
): SpellingIssue[] {
  const issues: SpellingIssue[] = [];
  let checked = 0;

  for (const [word, occurrences] of extractKoreanWords(text)) {
    if (checked >= MAX_WORDS_TO_CHECK) break;
    checked++;

    if (!checker.isCorrect(word)) {
      issues.push({
        word,
        occurrences,
        suggestions: checker.suggest(word).slice(0, MAX_SUGGESTIONS),
      });
    }
  }

  return issues;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replaces every occurrence of a word that stands on its own, i.e. is not
 * part of a longer Korean word. Links, mentions, hashtags and custom emoji
 * are left as they are, as they are not checked either.
 */
export function replaceWord(text: string, word: string, replacement: string) {
  const pattern = new RegExp(
    `(?<![\\uAC00-\\uD7A3])${escapeRegExp(word)}(?![\\uAC00-\\uD7A3])`,
    'gu',
  );

  let result = '';
  let last = 0;

  for (const match of text.matchAll(IGNORED_PATTERNS)) {
    const start = match.index;
    result += text.slice(last, start).replace(pattern, replacement) + match[0];
    last = start + match[0].length;
  }

  return result + text.slice(last).replace(pattern, replacement);
}
